import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { SupplierDetailLink } from "@/components/hub/HubEntityDetailLink";
import { EndUserDetailLink } from "@/components/inside-sales/EndUserDetailLink";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HubViewportCard } from "@/components/layout/HubViewportCard";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { SortableTableHeader, type SortDirection } from "@/components/ui/SortableTableHeader";
import { useHubToast } from "@/components/ui/HubToast";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { EM_DASH } from "@/lib/utils";

import { qualityCaseLabel } from "./casesApi";
import {
  correctiveActionSourceLabel,
  qualityEventSourceOptions,
  type CorrectiveActionSource,
} from "./correctiveActionsApi";
import { CreateQualityEventModal } from "./CreateQualityEventModal";
import {
  createQualityEvent,
  fetchQualityEvents,
  qualityEventStatusLabel,
  type QualityEvent,
  type QualityEventStatus,
} from "./qualityEventPageApi";

const HELP_TEXT = [
  "A quality event is the inciting record: a supplier issue, a complaint, an audit finding, or a floor find.",
  "Search and filter the list, then open an event for its notes, NCRs, and RCA.",
].join(" ");

const headerClassName = "sticky top-0 z-20 border-b border-slate-200";

type EventSortKey = "id" | "status" | "source" | "customer" | "summary" | "case" | "ncrCount" | "createdAt";
type StatusFilter = "all" | QualityEventStatus;

function formatDate(value: string | null): string {
  if (!value) return EM_DASH;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function eventSourceText(event: QualityEvent): string {
  if (event.sourceCategory != null) return correctiveActionSourceLabel(event.sourceCategory);
  return event.source ?? "";
}

function eventCustomerText(event: QualityEvent): string {
  if (event.sourceCategory === "customer_complaint") return event.customerName ?? "";
  if (event.sourceCategory === "supplier") return event.supplierName ?? "";
  return "";
}

function compareEvents(left: QualityEvent, right: QualityEvent, key: EventSortKey, dir: SortDirection): number {
  const direction = dir === "asc" ? 1 : -1;
  switch (key) {
    case "id":
      return (left.id - right.id) * direction;
    case "status":
      return qualityEventStatusLabel(left.status).localeCompare(qualityEventStatusLabel(right.status)) * direction;
    case "source":
      return eventSourceText(left).localeCompare(eventSourceText(right)) * direction;
    case "customer":
      return eventCustomerText(left).localeCompare(eventCustomerText(right)) * direction;
    case "summary":
      return (left.summary ?? "").localeCompare(right.summary ?? "") * direction;
    case "case":
      return ((left.qualityCaseId ?? 0) - (right.qualityCaseId ?? 0)) * direction;
    case "ncrCount":
      return ((left.ncrs ?? []).length - (right.ncrs ?? []).length) * direction;
    case "createdAt":
    default:
      return (new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime()) * direction;
  }
}

export function QualityEventsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const writesAllowed = useHubModuleWrite("quality");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sourceFilters, setSourceFilters] = useState<CorrectiveActionSource[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [sortKey, setSortKey] = useState<EventSortKey>("createdAt");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  const sourceQuery = sourceFilters.length > 0 ? [...sourceFilters].sort().join(",") : undefined;

  const listQuery = useQuery({
    queryKey: ["quality", "events", debouncedSearch, statusFilter, sourceQuery],
    queryFn: () =>
      fetchQualityEvents({
        search: debouncedSearch.trim() || undefined,
        status: statusFilter === "all" ? undefined : statusFilter,
        sourceCategory: sourceQuery,
        limit: 200,
      }),
  });

  const createMutation = useMutation({
    mutationFn: createQualityEvent,
    onSuccess: async (created) => {
      setCreateOpen(false);
      showToast({ tone: "success", message: `Created quality event ${created.id}.` });
      void queryClient.invalidateQueries({ queryKey: ["quality", "events"] });
      void navigate(`/quality/events/${created.id}`);
    },
    onError: (error: Error) => showToast({ tone: "error", message: error.message }),
  });

  const rows = useMemo(() => {
    const loaded = listQuery.data?.rows ?? [];
    return [...loaded].sort((left, right) => compareEvents(left, right, sortKey, sortDir));
  }, [listQuery.data?.rows, sortKey, sortDir]);

  const toggleSort = (key: EventSortKey) => {
    if (sortKey === key) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "source" || key === "customer" || key === "summary" || key === "status" ? "asc" : "desc");
  };

  const filtersActive = search.trim() !== "" || statusFilter !== "all" || sourceFilters.length > 0;
  const totalCount = listQuery.data?.totalCount ?? 0;
  const resultTitle = listQuery.isLoading
    ? "Loading quality events…"
    : rows.length < totalCount
      ? `Showing ${rows.length} of ${totalCount} quality events`
      : `${totalCount} quality event${totalCount === 1 ? "" : "s"}`;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint={HELP_TEXT}>Quality events</PageTitleWithHelp>
        {writesAllowed ? (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New event
          </Button>
        ) : null}
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <Input
              id="quality-event-search"
              className="min-w-[16rem] flex-1"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter by event, source, supplier, summary, case, or NCR"
              aria-label="Search quality events"
            />
            <div className="flex flex-wrap items-center gap-2">
              <p id="quality-event-status-filter" className="text-xs font-medium text-slate-600">
                Status
              </p>
              <SegmentedControl
                aria-labelledby="quality-event-status-filter"
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: "all", label: "All" },
                  { value: "open", label: "Open" },
                  { value: "closed", label: "Closed" },
                ]}
              />
            </div>
          </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-slate-600">Source</span>
              {qualityEventSourceOptions().map((source) => {
                const selected = sourceFilters.includes(source);
                return (
                  <Button
                    key={source}
                    type="button"
                    size="sm"
                    variant={selected ? "primary" : "secondary"}
                    aria-pressed={selected}
                    onClick={() =>
                      setSourceFilters((current) =>
                        current.includes(source) ? current.filter((value) => value !== source) : [...current, source],
                      )
                    }
                  >
                    {correctiveActionSourceLabel(source)}
                  </Button>
                );
              })}
              {sourceFilters.length > 0 ? (
                <Button type="button" size="sm" variant="ghost" onClick={() => setSourceFilters([])}>
                  Clear source filters
                </Button>
              ) : null}
            </div>
        </CardBody>
      </Card>

      <HubViewportCard>
        <CardHeader>
          <CardTitle>{resultTitle}</CardTitle>
        </CardHeader>
        <CardBody className="min-h-0 overflow-auto p-0">
          {listQuery.isError ? (
            <p className="p-4 text-sm text-red-700">{(listQuery.error as Error).message}</p>
          ) : listQuery.isLoading ? null : rows.length === 0 ? (
            <p className="p-4 text-sm text-slate-600">
              {filtersActive ? "No quality events match these filters." : "No quality events yet."}
            </p>
          ) : (
            <table className="min-w-full border-separate border-spacing-0 text-sm">
              <thead className="sticky top-0 z-10 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <SortableTableHeader className={headerClassName} label="Event" sortKey="id" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortableTableHeader
                    className={headerClassName}
                    label="Status"
                    sortKey="status"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                  />
                  <SortableTableHeader
                    className={headerClassName}
                    label="Source"
                    sortKey="source"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                  />
                  <SortableTableHeader
                    className={headerClassName}
                    label="Customer"
                    sortKey="customer"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                  />
                  <SortableTableHeader
                    className={headerClassName}
                    label="Summary"
                    sortKey="summary"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                  />
                  <SortableTableHeader className={headerClassName} label="Case" sortKey="case" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortableTableHeader
                    className={headerClassName}
                    label="NCRs"
                    sortKey="ncrCount"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    align="right"
                  />
                  <SortableTableHeader
                    className={headerClassName}
                    label="Opened"
                    sortKey="createdAt"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                  />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="cursor-pointer hover:bg-slate-50/80"
                    onClick={() => navigate(`/quality/events/${row.id}`)}
                  >
                    <td className="border-b border-slate-100 px-2 py-3 font-medium text-slate-900">
                      <Link
                        className="text-sky-700 hover:underline"
                        to={`/quality/events/${row.id}`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {row.id}
                      </Link>
                    </td>
                    <td className="border-b border-slate-100 px-2 py-3 text-slate-800">
                      {qualityEventStatusLabel(row.status)}
                    </td>
                    <td className="border-b border-slate-100 px-2 py-3 text-slate-800">
                      {eventSourceText(row) || EM_DASH}
                    </td>
                    <td
                      className="border-b border-slate-100 px-2 py-3 text-slate-800"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {row.sourceCategory === "customer_complaint" && (row.customerName ?? "").trim() !== "" ? (
                        <EndUserDetailLink
                          fsAccountId={row.customerFsAccountId}
                          endUserName={row.customerName}
                        />
                      ) : row.sourceCategory === "supplier" && row.supplierId != null && (row.supplierName ?? "").trim() !== "" ? (
                        <SupplierDetailLink supplierId={row.supplierId} supplierName={row.supplierName} />
                      ) : (
                        eventCustomerText(row) || EM_DASH
                      )}
                    </td>
                    <td className="border-b border-slate-100 px-2 py-3 text-slate-800">{row.summary ?? EM_DASH}</td>
                    <td className="border-b border-slate-100 px-2 py-3">
                      {row.qualityCaseId == null ? (
                        EM_DASH
                      ) : (
                        <Link
                          className="text-sky-700 hover:underline"
                          to={`/quality/cases/${row.qualityCaseId}`}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {qualityCaseLabel(row.qualityCaseId)}
                        </Link>
                      )}
                    </td>
                    <td className="border-b border-slate-100 px-2 py-3 text-right tabular-nums">{(row.ncrs ?? []).length}</td>
                    <td className="border-b border-slate-100 px-2 py-3">{formatDate(row.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </HubViewportCard>
      <CreateQualityEventModal
        open={createOpen}
        pending={createMutation.isPending}
        onClose={() => setCreateOpen(false)}
        onCreate={(input) => createMutation.mutate(input)}
      />
    </div>
  );
}
